import React from "react";
import { useAppSelector } from "../../../../services/hooks";
import { companyGuidSelector } from "../../../../services/selectors/auth.selectors";
import { useGetCompanyUsageDetailQuery } from "../../../../services/api/plansAdminApi";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "./planUsageTab.module.css";
import i18n from "../../../../i18n";

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString();
}

function usagePct(used: number, limit: number | null): number {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

function barClass(pct: number): string {
  if (pct >= 95) return `${styles.progressBar} ${styles.progressDanger}`;
  if (pct >= 80) return `${styles.progressBar} ${styles.progressWarning}`;
  return styles.progressBar;
}

export const PlanUsageTab: React.FC = () => {
  const companyGuid = useAppSelector(companyGuidSelector);
  const { data, isLoading, isError } = useGetCompanyUsageDetailQuery(companyGuid!, {
    skip: !companyGuid,
  });

  if (isLoading) {
    return <div className={styles.loading}>{t("LOADING")}</div>;
  }

  if (isError || !data) {
    return <div className={styles.loading}>Failed to load usage data.</div>;
  }

  const { company, features, users } = data;
  const companyPct = usagePct(company.used, company.limit);

  return (
    <div className={styles.wrapper}>
      {/* ── Company token usage ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>🤖</span>
          <div className={styles.sectionTitleGroup}>
            <span className={styles.sectionTitle}>{t("AI_TOKEN_USAGE_TITLE")}</span>
            <span className={styles.sectionSubtitle}>{t("AI_TOKEN_USAGE_RESETS_AT", { date: formatDate(company.resetsAt) })}</span>
          </div>
        </div>
        <div className={styles.sectionBody}>
          <div className={styles.quotaRow}>
            <div className={styles.quotaLabelRow}>
              <span className={styles.quotaLabel}>{t("AI_TOKEN_USAGE_COMPANY_TOTAL")}</span>
              <span className={styles.quotaNumbers}>
                {company.used.toLocaleString()} / {company.limit != null ? company.limit.toLocaleString() : "∞"}
              </span>
            </div>
            {company.limit != null && (
              <div className={styles.progressTrack}>
                <div className={barClass(companyPct)} style={{ width: `${companyPct}%` }} />
              </div>
            )}
            <div className={styles.quotaMeta}>
              {t("INPUT_TOKENS")}: {company.inputTokens.toLocaleString()} · {t("OUTPUT_TOKENS")}: {company.outputTokens.toLocaleString()} · {t("RESERVED_TOKENS")}: {company.reservedTokens.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* ── Per-feature breakdown ── */}
      {features.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>📊</span>
            <div className={styles.sectionTitleGroup}>
              <span className={styles.sectionTitle}>{t("AI_TOKEN_USAGE_BY_FEATURE")}</span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {features.map((f) => (
              <div key={f.featureKey} className={styles.quotaRow}>
                <div className={styles.quotaLabelRow}>
                  <span className={styles.quotaLabel}>{i18n.language === "en" ? f.featureNameEn : f.featureName ?? f.featureKey}</span>
                  <span className={styles.quotaNumbers}>{f.totalTokens.toLocaleString()} {t("TOKENS")}</span>
                </div>
                <div className={styles.quotaMeta}>
                  {t("INPUT_TOKENS")}: {f.inputTokens.toLocaleString()} · {t("OUTPUT_TOKENS")}: {f.outputTokens.toLocaleString()} · {t("CALL_COUNT_LABEL")}: {f.callCount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Per-user breakdown ── */}
      {users.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>👥</span>
            <div className={styles.sectionTitleGroup}>
              <span className={styles.sectionTitle}>{t("AI_TOKEN_USAGE_BY_USER")}</span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {users.map((u) => {
              const pct = usagePct(u.used, u.limit);
              return (
                <div key={u.userGuid} className={styles.quotaRow}>
                  <div className={styles.quotaLabelRow}>
                    <span className={styles.quotaLabel}>{u.userName ?? u.userGuid}</span>
                    <span className={styles.quotaNumbers}>
                      {u.used.toLocaleString()} / {u.limit != null ? u.limit.toLocaleString() : "∞"}
                    </span>
                  </div>
                  {u.limit != null && (
                    <div className={styles.progressTrack}>
                      <div className={barClass(pct)} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  <div className={styles.quotaMeta}>
                    {t("INPUT_TOKENS")}: {u.inputTokens.toLocaleString()} · {t("OUTPUT_TOKENS")}: {u.outputTokens.toLocaleString()} · {t("RESERVED_TOKENS")}: {u.reservedTokens.toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
